/**
 * Her outfits. The VRoid model's clothes are painted onto shared textures
 * (shirt + knit vest on one, skirt, loafers, ribbon, and the socks on the
 * skin texture), so an outfit is a recolour of those textures that keeps the
 * original shading and folds, plus a few procedural extras (tights, scarf).
 */
export type Outfit = {
  /** Blouse colour. */
  shirt: string;
  /** Knit vest colour (the same as the shirt reads as a plain blouse). */
  vest: string;
  skirt: string;
  /** Optional tartan lines over the skirt. */
  plaid?: string;
  /** Knee socks, or 'tights' for dark tights all the way up. */
  legs: { kind: 'socks' | 'tights'; color: string };
  shoes: string;
  /** School ribbon at the collar; null hides it. */
  ribbon: string | null;
  /** Knitted scarf round her neck. */
  scarf?: string;
};

export type OutfitName = keyof typeof OUTFITS;

export const OUTFITS = {
  /** The school uniform exactly as the model ships (no recolour). */
  uniform: null,
  /** The bookshop: cream blouse, camel vest, black skirt and tights. */
  work: {
    shirt: '#efe8da',
    vest: '#a27a52',
    skirt: '#26262b',
    legs: { kind: 'tights', color: '#1d1b1f' },
    shoes: '#1c1a1a',
    ribbon: null,
  },
  /** An ordinary weekday after work: pale blue blouse, grey vest, navy skirt, white socks. */
  casual: {
    shirt: '#d9e4f0',
    vest: '#5d6068',
    skirt: '#2c3550',
    legs: { kind: 'socks', color: '#e9e7e2' },
    shoes: '#5a3a26',
    ribbon: null,
  },
  /** Saturday night: white blouse, burgundy vest, black tartan skirt. */
  weekend: {
    shirt: '#f1efea',
    vest: '#6e2430',
    skirt: '#1e1e22',
    plaid: '#8a2a33',
    legs: { kind: 'tights', color: '#1b1a1d' },
    shoes: '#151414',
    ribbon: null,
  },
  /** School uniform with the winter beige cardigan-vest. */
  uniformWinter: {
    shirt: '#f2f1ee',
    vest: '#c8b48e',
    skirt: '#3a3d4a',
    plaid: '#5a5f73',
    legs: { kind: 'socks', color: '#25232a' },
    shoes: '#4a2f20',
    ribbon: '#2f6fb0',
  },
  /** First snow: forest-green knit, grey tartan, black tights, red scarf. */
  winter: {
    shirt: '#f0eee9',
    vest: '#2f4a3a',
    skirt: '#4a4c52',
    plaid: '#7a2e2e',
    legs: { kind: 'tights', color: '#1a191c' },
    shoes: '#161515',
    ribbon: null,
    scarf: '#a3262c',
  },
  /** A night out with friends: lilac blouse, black vest, wine tartan skirt. */
  nightOut: {
    shirt: '#d9cfe6',
    vest: '#1c1b20',
    skirt: '#4b1d2a',
    plaid: '#22171c',
    legs: { kind: 'tights', color: '#18171a' },
    shoes: '#141313',
    ribbon: null,
  },
} satisfies Record<string, Outfit | null>;

type Rgb = [number, number, number];

function rgb(hex: string): Rgb {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

const lum = (d: Uint8ClampedArray, i: number) => (0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]) / 255;

/**
 * Recolour pixels to `target`, keeping their shading: each pixel's brightness
 * relative to the region's average brightness scales the target colour.
 */
function tint(d: Uint8ClampedArray, i: number, target: Rgb, rel: number): void {
  d[i] = Math.min(255, target[0] * rel);
  d[i + 1] = Math.min(255, target[1] * rel);
  d[i + 2] = Math.min(255, target[2] * rel);
}

function readPixels(image: CanvasImageSource & { width: number; height: number }): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D; data: ImageData } {
  const canvas = document.createElement('canvas');
  canvas.width = image.width;
  canvas.height = image.height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
  ctx.drawImage(image, 0, 0);
  return { canvas, ctx, data: ctx.getImageData(0, 0, canvas.width, canvas.height) };
}

/** Run `paint` over every pixel, with each region's mean brightness worked out first. */
function repaint(
  image: CanvasImageSource & { width: number; height: number },
  classify: (x: number, y: number, l: number) => number,
  paint: (d: Uint8ClampedArray, i: number, region: number, rel: number, x: number, y: number) => void,
): HTMLCanvasElement {
  const { canvas, ctx, data } = readPixels(image);
  const d = data.data;
  const w = canvas.width;
  const h = canvas.height;
  const regions = new Int8Array(w * h);
  const sum: number[] = [];
  const count: number[] = [];
  for (let p = 0, i = 0; p < w * h; p += 1, i += 4) {
    const r = classify((p % w) / w, Math.floor(p / w) / h, lum(d, i));
    regions[p] = r;
    if (r < 0) continue;
    sum[r] = (sum[r] ?? 0) + lum(d, i);
    count[r] = (count[r] ?? 0) + 1;
  }
  for (let p = 0, i = 0; p < w * h; p += 1, i += 4) {
    const r = regions[p];
    if (r < 0) continue;
    const mean = sum[r] / count[r];
    paint(d, i, r, lum(d, i) / Math.max(0.02, mean), (p % w) / w, Math.floor(p / w) / h);
  }
  ctx.putImageData(data, 0, 0);
  return canvas;
}

type Img = CanvasImageSource & { width: number; height: number };

/** Shirt (sleeves, collar, placket) and knit vest. */
export function paintTops(image: Img, o: Outfit): HTMLCanvasElement {
  const shirt = rgb(o.shirt);
  const vest = rgb(o.vest);
  const SHIRT = 0;
  const VEST = 1;
  return repaint(
    image,
    // Top third is sleeves and collar; below it, the bright V of the shirt front sits inside the vest.
    (_x, y, l) => (y < 0.352 || l > 0.42 ? SHIRT : VEST),
    (d, i, r, rel) => tint(d, i, r === SHIRT ? shirt : vest, r === SHIRT ? rel * 0.97 : rel),
  );
}

/** The pleated skirt, with optional tartan. */
export function paintSkirt(image: Img, o: Outfit): HTMLCanvasElement {
  const base = rgb(o.skirt);
  const line = o.plaid ? rgb(o.plaid) : null;
  return repaint(
    image,
    (_x, _y, l) => (l < 0.04 ? -1 : 0),
    (d, i, _r, rel, x, y) => {
      tint(d, i, base, rel);
      if (!line) return;
      // Broad bands and a fine over-check, crossing darker where they meet.
      const u = (x * 22) % 1;
      const v = (y * 16) % 1;
      const band = (u < 0.18 ? 1 : 0) + (v < 0.18 ? 1 : 0);
      const fine = (u > 0.55 && u < 0.6) || (v > 0.55 && v < 0.6);
      const k = band === 2 ? 0.75 : band === 1 ? 0.5 : fine ? 0.35 : 0;
      if (k === 0) return;
      d[i] += (Math.min(255, line[0] * rel) - d[i]) * k;
      d[i + 1] += (Math.min(255, line[1] * rel) - d[i + 1]) * k;
      d[i + 2] += (Math.min(255, line[2] * rel) - d[i + 2]) * k;
    },
  );
}

/** One flat recolour (shoes, ribbon). */
export function paintSolid(image: Img, color: string): HTMLCanvasElement {
  const c = rgb(color);
  return repaint(
    image,
    (_x, _y, l) => (l < 0.03 ? -1 : 0),
    (d, i, _r, rel) => tint(d, i, c, rel),
  );
}

/** Socks or tights on the skin texture (legs are the two outer columns; socks are their dark lower ends and the feet). */
export function paintLegs(image: Img, o: Outfit): HTMLCanvasElement {
  const c = rgb(o.legs.color);
  const SOCK = 0;
  const LEG_SKIN = 1;
  const tights = o.legs.kind === 'tights';
  return repaint(
    image,
    (x, y, l) => {
      // Upper part of each column is the arm; legs start below the waistband seam.
      const legColumn = (x < 0.236 || x > 0.764) && y > 0.475;
      // Socks: the dark (not padding-black) parts of the legs and feet.
      if (l > 0.05 && l < 0.3 && (legColumn || (y > 0.55 && y < 0.8 && x > 0.22 && x < 0.78))) return SOCK;
      if (tights && legColumn && l >= 0.3) return LEG_SKIN;
      return -1;
    },
    (d, i, r, rel) => {
      if (r === SOCK) {
        tint(d, i, c, rel);
        return;
      }
      // Sheer: mostly the tights colour, a little skin tone showing through at the highlights.
      const sr = d[i];
      const sg = d[i + 1];
      const sb = d[i + 2];
      tint(d, i, c, rel * 1.15);
      d[i] = d[i] * 0.86 + sr * 0.14 * 0.45;
      d[i + 1] = d[i + 1] * 0.86 + sg * 0.14 * 0.45;
      d[i + 2] = d[i + 2] * 0.86 + sb * 0.14 * 0.45;
    },
  );
}
